import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Form, Modal, Select, Space, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import TextArea from "../../components/Inputs/TextAreaInput";
import { getAcademicPrograms } from "../../services/academicProgramService";
import {
  createSpecialisation,
  deleteSpecialisation,
  getSpecialisations,
  updateSpecialisation,
} from "../../services/specialisationService";
import { specialisationSchema } from "../../validators/specialisationValidators";
import { getInstitutions } from "../../services/institutionService";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const Specialisation = () => {
  const [specialisation, setSpecialisations] = useState([]);
  const [filteredSpecialisations, setFilteredSpecialisations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [specialisationToEdit, setSpecialisationToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [specialisationToDelete, setSpecialisationToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [academicPrograms, setAcademicPrograms] = useState([]);
  const [institutions, setInstitutions] = useState([]);

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadSpecialisations();
    loadAcademicPrograms();
    loadInstitutions();
  }, []);

  useEffect(() => {
    let filtered = specialisation;

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((item) =>
        item?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.academic_program?.institution?.id?.toString() ===
          selectedInstitution?.toString()
      );
    }

    setFilteredSpecialisations(filtered);
  }, [searchTerm, specialisation, selectedInstitution]);

  const loadAcademicPrograms = async () => {
    try {
      const { data } = await getAcademicPrograms();
      setAcademicPrograms(data.data);
    } catch (error) {
      toast.error("Failed to load academic programs");
    }
  };

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const loadSpecialisations = async () => {
    setLoading(true);
    try {
      const { data } = await getSpecialisations();
      setSpecialisations(data.data);
      setFilteredSpecialisations(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load specialisations"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (specialisationToEdit) {
      onUpdateSpecialisation(payload, resetForm);
    } else {
      onCreateSpecialisation(payload, resetForm);
    }
  };

  const onCreateSpecialisation = async (payload, resetForm) => {
    setErrs(null);
    try {
      const { data } = await createSpecialisation(payload);
      setSpecialisations((prev) => [...prev, data.data]);
      toast.success("Specialisation added successfully");
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Specialisation was not added, try again!"
        );
      }
    }
  };

  const handleEdit = (record) => {
    setSpecialisationToEdit(record);
    setIsModalOpen(true);
  };

  const onUpdateSpecialisation = async (payload, resetForm) => {
    setErrs(null);
    try {
      const original = [...specialisation];
      const index = original.findIndex(
        (item) => item.id === specialisationToEdit.id
      );
      if (index === -1) throw new Error("Specialisation not found.");

      const { data } = await updateSpecialisation(
        specialisationToEdit.id,
        payload
      );
      original[index] = data.data;
      setSpecialisations(original);
      setSpecialisationToEdit(null);
      resetForm();
      setIsModalOpen(false);
      toast.success("Specialisation updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Specialisation not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (record) => {
    setModalText(
      `Please confirm that you want to delete %${record?.name?.toUpperCase()}`
    );
    setSpecialisationToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const onDeleteSpecialisation = async () => {
    const original = [...specialisation];
    const updated = original.filter(
      (item) => item.id !== specialisationToDelete.id
    );
    setSpecialisations(updated);
    try {
      await deleteSpecialisation(specialisationToDelete?.id);
      toast.success("Specialisation deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        response?.data?.message || "Specialisation not deleted, try again later"
      );
      setSpecialisations(original);
    } finally {
      setIsDeleteModalOpen(false);
      setSpecialisationToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setSpecialisationToEdit(null);
    setSpecialisationToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (_, __, index) => index + 1,
      width: 70,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => (
        <Tooltip title={text}>
          <div className="truncate max-w-xs">{text || "N/A"}</div>
        </Tooltip>
      ),
    },
    {
      title: "Academic Program",
      dataIndex: ["academic_program", "name"],
      key: "academic_program",
      render: (text) => (
        <span className="text-gray-700 text-sm">{text || "N/A"}</span>
      ),
    },

    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["academic_program", "institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Specialisation">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Specialisation">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Specialisations" subtitle1="Specialisations" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Specialisations"
            count={filteredSpecialisations.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Specialisation"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            loading={loading}
            columns={columns}
            rows={filteredSpecialisations.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              !isInstAdmin && (
                <div className="mb-2 py-2 flex gap-2">
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => {
                      setSelectedInstitution(value);
                    }}
                    showSearch={true}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value={"Not Selected"}>
                      Filter by Institution
                    </Select.Option>
                    {institutions &&
                      institutions.length > 0 &&
                      institutions.map((option) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                </div>
              )
            }
          />

          {/* Modal */}
          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {specialisationToEdit
                    ? "Edit Specialisation"
                    : "Add Specialisation"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: specialisationToEdit?.name || "",
                description: specialisationToEdit?.description || "",
                academic_program_id:
                  specialisationToEdit?.academic_program?.id || "",

                institution_id: specialisationToEdit
                  ? specialisationToEdit?.academic_program?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
              }}
              enableReinitialize
              validationSchema={specialisationSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                submitForm,
              }) => (
                <Form className="space-y-3 px-4! py-4!">
                  {errs && <FormErrors errors={errs} />}

                  {isInstAdmin ? (
                    <input
                      type="hidden"
                      name="institution_id"
                      value={
                        specialisationToEdit
                          ? specialisationToEdit?.institution?.id
                          : user?.institution?.id
                      }
                    />
                  ) : (
                    <CustomDropDownInput
                      name="institution_id"
                      title="Institution"
                      placeholder="Select an institution"
                      requiredField
                      errors={errors}
                      values={values}
                      touched={touched}
                      displayLabel="name"
                      idLabel="id"
                      options={institutions}
                      setFieldValue={setFieldValue}
                    />
                  )}

                  <CustomDropDownInput
                    name="academic_program_id"
                    title="Academic Program"
                    placeholder="Select an academic program"
                    requiredField
                    errors={errors}
                    values={values}
                    touched={touched}
                    displayLabel="name"
                    idLabel="id"
                    options={academicPrograms.filter(
                      (p) => p.institution?.id === values.institution_id
                    )}
                    setFieldValue={setFieldValue}
                  />

                  <TextInput
                    name="name"
                    title="Specialisation Name"
                    placeholder="e.g. Artificial Intelligence"
                    requiredField
                    errors={errors}
                    values={values}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                  />

                  <TextArea
                    name="description"
                    title="Description"
                    placeholder="Enter description"
                    errors={errors}
                    values={values}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                  />

                  <div className="flex justify-end gap-2">
                    <FormCancelButton text="Cancel" onClick={handleCancel} />
                    <FormButton
                      text="Save"
                      type="primary"
                      onClick={submitForm}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteSpecialisation}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default Specialisation;
